// Service for managing users using Firebase Firestore
import { db } from '../firebase/firebase-config';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot, 
  serverTimestamp,
  query,
  orderBy 
} from 'firebase/firestore';

export interface User {
  id?: string;
  name: string;
  email: string;
  role: string;
  status: string;
  lastLogin: string;
  permissions: string[];
  phone?: string;
  department?: string;
  notes?: string;
  createdAt?: any;
  updatedAt?: any;
  isDeleted?: boolean;
}

// Subscribe to real-time user updates
export const subscribeToUsers = (
  callback: (users: User[]) => void,
  onError?: (error: any) => void
): (() => void) => {
  try {
    console.log('👥 Setting up Firebase real-time listener for users...');
    
    const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
    
    return onSnapshot(q, (snapshot) => {
      console.log('👥 Firebase users snapshot received, docs count:', snapshot.docs.length);
      
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        } as User;
      });

      // Filter out deleted users
      const activeUsers = users.filter(user => !user.isDeleted);
      
      console.log('✅ Processed active users:', activeUsers.length);
      callback(activeUsers);
    }, (error) => {
      console.error('❌ Firebase users subscription error:', error);
      if (onError) {
        onError(error);
      }
    });
  } catch (error) {
    console.error('❌ Failed to setup users subscription:', error);
    if (onError) {
      onError(error);
    }
    return () => {};
  }
};

// Add new user
export const addUser = async (user: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    console.log('👥 Adding user to Firebase:', user.name);
    
    const userData = {
      ...user,
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, "users"), userData);
    console.log('✅ User added to Firebase with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Failed to add user:', error);
    throw error;
  }
};

// Update user
export const updateUser = async (id: string, updates: Partial<User>): Promise<void> => {
  try {
    console.log('👥 Updating user in Firebase:', id);
    
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, "users", id), updateData);
    console.log('✅ User updated in Firebase:', id);
  } catch (error) {
    console.error('❌ Failed to update user:', error);
    throw error;
  }
};

// Soft delete user
export const softDeleteUser = async (id: string): Promise<void> => {
  try {
    console.log('👥 Soft deleting user from Firebase:', id);
    
    await updateDoc(doc(db, "users", id), {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ User soft deleted in Firebase:', id);
  } catch (error) {
    console.error('❌ Failed to soft delete user:', error);
    throw error;
  }
};

// Get all users (one-time fetch)
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, "users"));
    return querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as User))
      .filter(user => !user.isDeleted);
  } catch (error) {
    console.error('❌ Failed to get users:', error);
    throw error;
  }
};

// Standard users to migrate
export const standardUsers: Omit<User, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'John Admin',
    email: 'admin@naim.com',
    role: 'Admin',
    status: 'Active',
    lastLogin: '2024-01-20',
    permissions: ['candidates', 'jobs', 'tasks', 'appointments', 'reports', 'settings'],
    phone: '+254-700-000-001',
    department: 'Management',
    notes: 'System Administrator'
  },
  {
    name: 'Sarah Broker',
    email: 'sarah@naim.com',
    role: 'Broker',
    status: 'Active',
    lastLogin: '2024-01-19',
    permissions: ['candidates', 'jobs', 'tasks', 'appointments'],
    phone: '+254-700-000-002',
    department: 'Operations',
    notes: 'Senior Broker'
  },
  {
    name: 'Mike Receptionist',
    email: 'mike@naim.com',
    role: 'Receptionist',
    status: 'Active',
    lastLogin: '2024-01-18',
    permissions: ['candidates', 'appointments'],
    phone: '+254-700-000-003',
    department: 'Front Desk',
    notes: 'Reception and customer service'
  }
];

// Migrate standard users to Firebase
export const migrateStandardUsers = async (): Promise<void> => {
  console.log('👥 Starting user data migration to Firebase...');
  
  try {
    for (let i = 0; i < standardUsers.length; i++) {
      const user = standardUsers[i];
      console.log(`👥 Adding user ${i + 1}/${standardUsers.length}: ${user.name}`);
      
      try {
        await addUser(user);
        console.log(`✅ Successfully added ${user.name}`);
      } catch (error) {
        console.error(`❌ Failed to add ${user.name}:`, error);
      }
    }
    
    console.log('✅ User migration completed successfully!');
    alert('Standard users have been added to Firebase. User management will now sync across all devices.');
  } catch (error) {
    console.error('❌ User migration failed:', error);
    alert('User migration failed. Please check the console for details.');
  }
};